import * as Sentry from '@sentry/react'
import React from 'react'
import ReactDOM from 'react-dom/client'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import { Toaster, ToastBar } from 'react-hot-toast'
import { Toast as ArtemToast } from '@/common'
import { initClarityApp } from '@/shared/analytics/claritySession'
import ErrorPage from './ErrorPage'
import Root from '@/app/Root'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/api/queryClient'
import './i18n'
import './index.css'

initClarityApp()

function initSentry() {
  const enableReplay = import.meta.env.VITE_SENTRY_REPLAY_ENABLED === 'true'

  const integrations = [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ]

  if (enableReplay) {
    integrations.push(Sentry.replayIntegration())
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN!,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT!,
    integrations,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  })
}

initSentry()

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
const router = sentryCreateBrowserRouter([
  {
    path: '/*',
    element: <Root />,
    errorElement: <ErrorPage />
  }
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Toaster position="top-right">
      {t => (
        <ToastBar style={{ padding: 0, boxShadow: 'none' }} toast={t}>
          {({ message }) => <ArtemToast message={message} type={t.type} />}
        </ToastBar>
      )}
    </Toaster>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
)
